// String cleaning/normalisation helpers, ported from importCSVs.gs.
// Browser equivalents of the Apps Script HtmlService/Sheets-specific helpers.

/** Decode HTML entities (e.g. "&#39;" -> "'") using the DOM. */
export function decodeHtmlEntities(text) {
  if (!text) return text === 0 ? '0' : '';
  const el = document.createElement('textarea');
  el.innerHTML = String(text);
  return el.value;
}

/**
 * Strip a leading single quote. The legacy importer prefixed values with `'`
 * (forceTextForSheet / cleanPostcode / phone) purely to stop Google Sheets
 * auto-converting them. Firestore needs the clean value.
 */
export function stripLeadingApostrophe(value) {
  if (value == null) return value;
  const s = String(value);
  return s.startsWith("'") ? s.slice(1) : s;
}

const UNWANTED_PRODUCT_PATTERNS = [
  /\[.*?\]/g, // text in square brackets
  /Felt sleeve compatible with/gi,
  /Felt sleeve for/gi,
  /compatible with all models/gi,
  /PERFECT FIT/gi,
  /UK MADE/gi,
  /12 Great Colours/gi,
];

/** Strip marketing boilerplate from a product title (cleanProduct in importCSVs.gs). */
export function cleanProduct(product) {
  if (!product) return '';
  let cleaned = String(product);
  UNWANTED_PRODUCT_PATTERNS.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, '');
  });
  cleaned = cleaned.replace(/\s{2,}/g, ' ');
  while (/[,.\s!]+$/.test(cleaned)) {
    cleaned = cleaned.replace(/[,.\s!]+$/, '');
  }
  return cleaned.trim();
}

const UK_COUNTRY_VALUES = ['UNITED KINGDOM', 'UK', 'GB'];

/**
 * Normalise a postcode (cleanPostcode in importCSVs.gs).
 * For UK: uppercase, remove spaces, re-insert a space before the last 3 chars
 * ("TN279EN" -> "TN27 9EN"). For non-UK: return as-is (legacy `'`-prefix dropped).
 */
export function cleanPostcode(postcode, country) {
  if (!postcode) return '';
  const isUK = country && UK_COUNTRY_VALUES.includes(String(country).toUpperCase());
  if (isUK) {
    const pc = String(postcode).toUpperCase().replace(/\s+/g, '');
    if (pc.length <= 3) return pc;
    return `${pc.slice(0, -3)} ${pc.slice(-3)}`;
  }
  return String(postcode).trim();
}

const ADDRESS_FIELDS = ['address1', 'address2', 'townCity', 'county', 'postcode', 'country'];

/**
 * Blank out any address field that exactly duplicates an earlier address field
 * (removeAddressDuplicates in importCSVs.gs). Operates on a shipping-like object
 * with the keys in ADDRESS_FIELDS; returns a new object.
 */
export function removeAddressDuplicates(shipping) {
  const seen = new Set();
  const out = { ...shipping };
  ADDRESS_FIELDS.forEach((key) => {
    const val = out[key];
    if (val && !seen.has(val)) {
      seen.add(val);
    } else if (val) {
      out[key] = '';
    }
  });
  return out;
}

/**
 * Normalise a UK phone number to leading "0" form, no leading apostrophe.
 * Mirrors the Shopify cleaning (and the eBay "+44 " -> "0" replacement).
 */
export function normalisePhone(value) {
  if (!value) return '';
  let phone = String(value).trim().replace(/^'+/, '');
  phone = phone.replace(/^(?:\+44|44|0044|00\s?44)\s?/, '0');
  if (/^7/.test(phone)) phone = `0${phone}`;
  return phone;
}

/** Strip currency symbols/whitespace and parse to a number (0 if not parseable). */
export function parseMoney(value) {
  if (value == null || value === '') return 0;
  const n = parseFloat(String(value).replace(/[$£€\s,]/g, ''));
  return Number.isNaN(n) ? 0 : n;
}
